import {
  removeStream,
  setLocalStream,
  setShowLoader,
  toggleCam,
  toggleMic,
  setMic,
  setShowPermissionModal
} from "../slices/roomSlice";
import store from "./../store/index";
import * as wss from "./wss";
import Peer from "simple-peer";
import { setStreams } from "./../slices/roomSlice";
import { has } from "lodash";
import { attachBotAudioStream, detachBotAudioStream } from "./botAudioBridge";
let hasCamera = false;
let hasMic = true;

/**
 * Embedded call initiator: mute real mic before signaling so the recorder/bot never receives
 * caller audio until Room.js unmutes when a real agent is present (humanParticipants > 1).
 * Bot/agent joins use isRoomHost:false + attendee_token — excluded here.
 */
function shouldMuteCallerMicUntilAgent(data) {
  if (!data) return false;
  if (data.rejoinAsCaller) return true;
  return Boolean(data.isRoomHost && data.url);
}

const defaultConstraints = {
  audio: true,
  video: true,
};

const onlyAudioConstraints = {
  audio: true,
  video: false,
};

let localStream;

// Single source of truth for peer connections
const peers = {};

// WebRTC auto-repair: disconnected > N ms → ICE restart; failed/closed → cleanup + caller rejoin
const DISCONNECTED_REPAIR_MS = 8000;
const peerDisconnectedTimeouts = {};
const peerIceRestartAttempted = {};

function tryIceRestart(peer, connectedUserSocketId) {
  if (!peer || !peer._pc || !peer.initiator) return;
  if (peerIceRestartAttempted[connectedUserSocketId]) return;
  peerIceRestartAttempted[connectedUserSocketId] = true;
  const pc = peer._pc;
  if (typeof pc.restartIce === "function") {
    pc.restartIce();
  }
  pc
    .createOffer({ iceRestart: true })
    .then((offer) => pc.setLocalDescription(offer))
    .then(() => {
      wss.signalPeerData({
        connectedUserSocketId,
        signal: pc.localDescription,
      });
    })
    .catch((err) => console.error("ICE restart failed", err));
}

function cleanupPeerConnection(socketId) {
  const peer = peers[socketId];
  detachBotAudioStream(socketId, peer);
  // An old peer's close callbacks must not remove a replacement audio stream.
  delete peers[socketId];
  if (peerDisconnectedTimeouts[socketId]) clearTimeout(peerDisconnectedTimeouts[socketId]);
  const videoEl = document.getElementById(socketId);
  if (videoEl) {
    try {
      const tracks = videoEl.srcObject?.getTracks();
      if (tracks) tracks.forEach((t) => t.stop());
      videoEl.srcObject = null;
      videoEl.parentNode.removeChild(videoEl);
    } catch (e) {
      // ignore
    }
  }
  if (peer) {
    try {
      peer.destroy();
    } catch (e) {
      // ignore
    }
  }
  delete peerDisconnectedTimeouts[socketId];
  delete peerIceRestartAttempted[socketId];
  store.dispatch(removeStream(socketId));
}

function handlePeerConnectionFailed(socketId) {
  if (!peers[socketId]) return;
  cleanupPeerConnection(socketId);
  const { fromUrl, roomId, identity } = store.getState().room;
  if (fromUrl && roomId && identity) {
    wss.callerRejoin(roomId, identity);
  }
}

function attachPeerConnectionStateHandlers(peer, connectedUserSocketId) {
  if (!peer || !peer._pc) return;
  const pc = peer._pc;

  pc.oniceconnectionstatechange = () => {
    if (peers[connectedUserSocketId] !== peer) return;
    const state = pc.iceConnectionState;
    if (state === "connected" || state === "completed") {
      peerIceRestartAttempted[connectedUserSocketId] = false;
      if (peerDisconnectedTimeouts[connectedUserSocketId]) {
        clearTimeout(peerDisconnectedTimeouts[connectedUserSocketId]);
        delete peerDisconnectedTimeouts[connectedUserSocketId];
      }
      return;
    }
    if (state === "disconnected") {
      if (peerDisconnectedTimeouts[connectedUserSocketId]) return;
      peerDisconnectedTimeouts[connectedUserSocketId] = setTimeout(() => {
        delete peerDisconnectedTimeouts[connectedUserSocketId];
        if (peers[connectedUserSocketId]?._pc?.iceConnectionState === "disconnected") {
          tryIceRestart(peers[connectedUserSocketId], connectedUserSocketId);
        }
      }, DISCONNECTED_REPAIR_MS);
      return;
    }
    if (state === "failed" || state === "closed") {
      if (peerDisconnectedTimeouts[connectedUserSocketId]) {
        clearTimeout(peerDisconnectedTimeouts[connectedUserSocketId]);
        delete peerDisconnectedTimeouts[connectedUserSocketId];
      }
      handlePeerConnectionFailed(connectedUserSocketId);
    }
  };

  pc.onconnectionstatechange = () => {
    if (peers[connectedUserSocketId] !== peer) return;
    const state = pc.connectionState;
    if (state === "failed" || state === "closed") {
      if (peerDisconnectedTimeouts[connectedUserSocketId]) {
        clearTimeout(peerDisconnectedTimeouts[connectedUserSocketId]);
        delete peerDisconnectedTimeouts[connectedUserSocketId];
      }
      handlePeerConnectionFailed(connectedUserSocketId);
    }
  };
}

// Function to add an empty video track
function addEmptyVideoTrack(stream) {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 640;
    canvas.height = 480;
    const canvasStream = canvas.captureStream(30);
    const [videoTrack] = canvasStream.getVideoTracks();
    stream.addTrack(videoTrack);
  } catch (error) {
    console.error("Error occurred when adding empty video track", error);
  }
}

// Function to add an empty audio track
function addEmptyAudioTrack(stream) {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const destination = audioContext.createMediaStreamDestination();
    oscillator.connect(destination);
    oscillator.start();
    const [audioTrack] = destination.stream.getAudioTracks();
    stream.addTrack(audioTrack);
  } catch (error) {
    console.error("Error occurred when adding empty audio track", error);
  }
}

export const getLocalPreviewAndInitRoomConnection = async (data) => {
  try {
    // Check if permissions are already granted
    const permissions = await navigator.permissions.query({ name: 'camera' });
    const micPermissions = await navigator.permissions.query({ name: 'microphone' });
    
    // Only show permission modal if permissions are not already granted
    if (permissions.state === 'prompt' || micPermissions.state === 'prompt') {
      store.dispatch(setShowPermissionModal(true));
      return;
    }

    // If permissions are already granted or after user clicks allow, proceed with getting devices
    return await initializeDevicesAndConnection(data);
  } catch (error) {
    console.error("Error checking permissions:", error);
    // If we can't check permissions, show the modal to be safe
    store.dispatch(setShowPermissionModal(true));
    return;
  }
};

// Separate function to handle device initialization and connection
export const initializeDevicesAndConnection = async (data) => {
  return new Promise((resolve, reject) => {
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        // Filter devices to find video input devices (cameras)
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput"
        );

        const audioDevices = devices.filter(
          (device) => device.kind === "audioinput"
        );

        if (audioDevices.length === 0) {
          console.log("No audio devices found");
          hasMic = false;
          store.dispatch(setMic(false));
        }

        if (videoDevices.length > 0) {
          console.log("Camera found");
          // At least one camera is available
          hasCamera = true;
        } else {
          console.log("No camera found");
          // No camera is available
          hasCamera = false;
        }

        if (!hasMic && !hasCamera) {
          // Neither mic nor camera is available, handle the case here
          // You can create empty tracks and proceed or show a message to the user
          localStream = new MediaStream();
          addEmptyAudioTrack(localStream);
          addEmptyVideoTrack(localStream);
          showLocalPreview(localStream);

          if (shouldMuteCallerMicUntilAgent(data)) {
            setMicrophoneEnabled(false);
          }

          store.dispatch(setShowLoader(false));
          if (data.rejoinAsCaller) {
            wss.callerRejoin(data.roomId, data.identity);
          } else if (data.isRoomHost) {
            wss.createNewRoom(data);
          } else {
            wss.joinRoom(data.roomId, data.identity, data.attendee_token, data.is_selected_agent);
          }
          resolve();
          return; // Skip further processing
        }
      })
      .then(() => {
        const constraints = {
          audio: hasMic,
          video: hasCamera,
        };

        navigator.mediaDevices
          .getUserMedia(constraints)
          .then((stream) => {
            store.dispatch(setShowLoader(false));

            localStream = stream;

            if (!hasCamera) {
              addEmptyVideoTrack(localStream); // Add an empty video track if no camera
            }

            if (!hasMic) {
              addEmptyAudioTrack(localStream); // Add an empty audio track if no microphone
            }

            showLocalPreview(stream);

            const audioTrack = stream.getAudioTracks()[0];
            if (audioTrack) attachLocalAudioTrackListeners(audioTrack);

            if (shouldMuteCallerMicUntilAgent(data)) {
              setMicrophoneEnabled(false);
            }

            if (data.onlyWithAudio) {
              toggleVideo();
            }
            if (data.rejoinAsCaller) {
              wss.callerRejoin(data.roomId, data.identity);
            } else if (data.isRoomHost) {
              wss.createNewRoom(data);
            } else {
              wss.joinRoom(data.roomId, data.identity, data.attendee_token, data.is_selected_agent);
            }
            resolve();
          })
          .catch((error) => {
            store.dispatch(setShowLoader(false));
            console.error("Error occurred in getting local stream", error);
            reject(error);
          });
      })
      .catch((error) => {
        console.error("Error enumerating devices:", error);
        reject(error);
      });
  });
};

const getConfiguration = () => {
  return {
    iceServers: [
      {
        urls: "stun:stun.l.google.com:19302",
      },
      {
        urls: "turn:3.142.183.117:3478",
        username: "iconnect",
        credential: "Iconnect@123",
      },
    ],
  };
};

export const prepareNewPeerConnection = (connectedUserSocketId, isInitiator) => {
  try {
    if (peers[connectedUserSocketId]) {
      const previousPeer = peers[connectedUserSocketId];
      detachBotAudioStream(connectedUserSocketId, previousPeer);
      delete peers[connectedUserSocketId];
      if (peerDisconnectedTimeouts[connectedUserSocketId]) {
        clearTimeout(peerDisconnectedTimeouts[connectedUserSocketId]);
      }
      delete peerDisconnectedTimeouts[connectedUserSocketId];
      delete peerIceRestartAttempted[connectedUserSocketId];
      try {
        previousPeer.destroy();
      } catch (e) {
        // ignore
      }
      store.dispatch(removeStream(connectedUserSocketId));
    }

    const configuration = getConfiguration();

    const peer = new Peer({
      initiator: isInitiator,
      config: configuration,
      stream: localStream,
    });
    peers[connectedUserSocketId] = peer;

    peer.on("signal", (signal) => {
      if (peers[connectedUserSocketId] !== peer) return;
      const signalData = {
        connectedUserSocketId,
        signal,
      };
      wss.signalPeerData(signalData);
    });

    peer.on("stream", (stream) => {
      if (peers[connectedUserSocketId] !== peer) return;
      attachBotAudioStream(connectedUserSocketId, stream, peer);
      addStream(stream, connectedUserSocketId);
      store.dispatch(setStreams(stream, connectedUserSocketId));
    });

    peer.on("track", (track, stream) => {
      if (peers[connectedUserSocketId] !== peer || track.kind !== "audio" || !stream) return;
      attachBotAudioStream(connectedUserSocketId, stream, peer);
    });
    peer.on("close", () => detachBotAudioStream(connectedUserSocketId, peer));
    peer.on("error", () => detachBotAudioStream(connectedUserSocketId, peer));

    attachPeerConnectionStateHandlers(peer, connectedUserSocketId);
  } catch (error) {
    console.error("Error occurred when preparing new peer connection", error);
  }
};

export const handleSinalingData = (data) => {
  try {
    const { connectedUserSocketId, signal } = data;
    peers[connectedUserSocketId].signal(signal);
  } catch (error) {
    console.error("Error occurred when handling signaling data", error);
  }
};

export const removePeerConnection = (data) => {
  try {
    const { socketId } = data;
    cleanupPeerConnection(socketId);

    const { participants, isRoomHost } = store.getState().room;
    if (isRoomHost && participants.length < 3) {
      setTimeout(() => {
        window.close();
      }, 5000);
    }
  } catch (error) {
    console.error("Error occurred when removing peer connection", error);
  }
};

const addStream = (stream, connectedUserSocketId) => {
  // Implementation for adding stream to video element
};

const showLocalPreview = (stream) => {
  store.dispatch(setLocalStream(stream));
};

let resumeDebounceTimer = null;

function isAudioTrackBroken() {
  if (!localStream) return false;
  const audioTracks = localStream.getAudioTracks();
  if (audioTracks.length === 0) return false;
  const track = audioTracks[0];
  // Do not treat policy/user mute (!enabled) as "broken" — recovery would replace the track
  // with a fresh enabled mic and leak audio to peers/recorder.
  if (track.readyState === "ended") return true;
  return Boolean(track.muted && track.enabled);
}

function replaceTracksOnAllPeers(stream, { replaceAudio = true, replaceVideo = true } = {}) {
  for (const socketId in peers) {
    const peer = peers[socketId];
    if (!peer?._pc) continue;
    const senders = peer._pc.getSenders();
    senders.forEach((sender) => {
      if (replaceAudio && sender.track?.kind === "audio" && stream.getAudioTracks()[0]) {
        sender.replaceTrack(stream.getAudioTracks()[0]);
      }
      if (replaceVideo && sender.track?.kind === "video" && stream.getVideoTracks()[0]) {
        sender.replaceTrack(stream.getVideoTracks()[0]);
      }
    });
  }
}

/**
 * After app resume / back online / phone call end: re-get getUserMedia
 * if audio track is broken (ended, hardware-muted, or disabled by OS),
 * then replace tracks on all peer senders.
 *
 * Options:
 *   forceAudio – always re-acquire audio even if track looks alive.
 *                Use on mobile resume where hardware mute is invisible.
 */
export const ensureLocalStreamAfterResume = ({ forceAudio = false } = {}) => {
  if (!localStream) return Promise.resolve();
  const { screenShareActive } = store.getState().room;
  const audioBroken = isAudioTrackBroken();
  const videoEnded = localStream.getVideoTracks().some((t) => t.readyState === "ended");
  if (!forceAudio && !audioBroken && !videoEnded) return Promise.resolve();

  const needAudio = forceAudio || audioBroken;
  // While sharing screen, never recover/replace camera video on senders here.
  // Doing so can overwrite the outbound screen track with camera/black frames.
  const needVideo = screenShareActive ? false : videoEnded;
  if (!needAudio && !needVideo) return Promise.resolve();

  const constraints = {};
  if (needAudio && hasMic) constraints.audio = true;
  if (needVideo && hasCamera) constraints.video = true;
  if (Object.keys(constraints).length === 0) return Promise.resolve();

  return navigator.mediaDevices
    .getUserMedia(constraints)
    .then((freshStream) => {
      const newAudioTrack = freshStream.getAudioTracks()[0];
      const newVideoTrack = freshStream.getVideoTracks()[0];

      if (newAudioTrack) {
        // Apply mute before the replacement reaches any peer, including the recorder bot.
        newAudioTrack.enabled = !!store.getState().room.micState;
        localStream.getAudioTracks().forEach((t) => {
          t.stop();
          localStream.removeTrack(t);
        });
        localStream.addTrack(newAudioTrack);
        attachLocalAudioTrackListeners(newAudioTrack);
      }

      if (newVideoTrack) {
        localStream.getVideoTracks().forEach((t) => {
          t.stop();
          localStream.removeTrack(t);
        });
        localStream.addTrack(newVideoTrack);
      }

      store.dispatch(setLocalStream(localStream));
      replaceTracksOnAllPeers(localStream, {
        replaceAudio: !!newAudioTrack,
        replaceVideo: !screenShareActive && !!newVideoTrack,
      });
      const wantMicOn = store.getState().room.micState;
      const outboundAudio = localStream.getAudioTracks()[0];
      if (outboundAudio) outboundAudio.enabled = !!wantMicOn;
    })
    .catch((err) => console.error("ensureLocalStreamAfterResume failed", err));
};

/**
 * Resume all remote <audio> elements that may have been paused by the OS
 * (e.g. incoming phone call on mobile). Safe to call at any time;
 * .play() on an already-playing element is a no-op.
 */
export const resumeAllRemoteAudio = () => {
  try {
    document.querySelectorAll("audio[autoplay]").forEach((el) => {
      if (el.paused && el.srcObject) {
        el.play().catch(() => {});
      }
    });
  } catch (_) {}
};

/**
 * Debounced handler for app resume events.
 * Multiple events (visibilitychange, focus, pageshow) can fire close together;
 * we only need to run the recovery once.
 */
function handleAppResume() {
  if (!localStream || Object.keys(peers).length === 0) return;
  if (resumeDebounceTimer) return;
  resumeDebounceTimer = setTimeout(() => {
    resumeDebounceTimer = null;
  }, 2000);

  ensureLocalStreamAfterResume({ forceAudio: true }).then(() => {
    resumeAllRemoteAudio();
  });
}

/**
 * Listen for app resume via multiple events.
 * - visibilitychange: tab switch, app backgrounded
 * - focus: returning from phone call (WebView), tab refocus
 * - pageshow: bfcache restore, some iOS WebView transitions
 */
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") handleAppResume();
  });
}
if (typeof window !== "undefined") {
  window.addEventListener("focus", handleAppResume);
  window.addEventListener("pageshow", (e) => {
    if (e.persisted) handleAppResume();
  });
}

/**
 * Attach listeners to the local audio track so we can detect when the OS
 * kills or mutes it (e.g. incoming phone call). On 'ended' or prolonged 'mute',
 * we re-acquire the mic and replace the sender track.
 */
let audioMuteTimer = null;
const AUDIO_MUTE_RECOVERY_MS = 3000;

function attachLocalAudioTrackListeners(track) {
  if (!track || track._recoveryBound) return;
  track._recoveryBound = true;

  track.addEventListener("ended", () => {
    if (Object.keys(peers).length === 0) return;
    ensureLocalStreamAfterResume({ forceAudio: true }).then(() => {
      resumeAllRemoteAudio();
    });
  });

  track.addEventListener("mute", () => {
    if (Object.keys(peers).length === 0) return;
    if (audioMuteTimer) clearTimeout(audioMuteTimer);
    audioMuteTimer = setTimeout(() => {
      audioMuteTimer = null;
      if (isAudioTrackBroken() && Object.keys(peers).length > 0) {
        ensureLocalStreamAfterResume({ forceAudio: true }).then(() => {
          resumeAllRemoteAudio();
        });
      }
    }, AUDIO_MUTE_RECOVERY_MS);
  });

  track.addEventListener("unmute", () => {
    if (audioMuteTimer) {
      clearTimeout(audioMuteTimer);
      audioMuteTimer = null;
    }
  });
}

export const toggleMicrophone = () => {
  const isMicEnabled = localStream.getAudioTracks()[0].enabled;
  const storeLocalStream = store.getState().room.localStream;
  const nextEnabled = !isMicEnabled;
  store.dispatch(toggleMic(nextEnabled));
  storeLocalStream.getAudioTracks()[0].enabled = nextEnabled;
  wss.syncMicMutedToServer(!nextEnabled);
};

export const setMicrophoneEnabled = (enabled) => {
  const storeLocalStream = store.getState().room.localStream || localStream;
  if (!storeLocalStream) {
    return false;
  }
  const audioTrack = storeLocalStream.getAudioTracks()[0];
  if (!audioTrack) {
    return false;
  }
  const nextEnabled = !!enabled;
  const trackChanged = audioTrack.enabled !== nextEnabled;
  audioTrack.enabled = nextEnabled;
  store.dispatch(toggleMic(nextEnabled));
  if (trackChanged) {
    wss.syncMicMutedToServer(!nextEnabled);
  }
  return true;
};

export const toggleVideo = () => {
  if (localStream?.getVideoTracks().length > 0) {
    const isVideoEnabled = localStream?.getVideoTracks()[0]?.enabled || false;
    const storeLocalStream = store.getState().room.localStream;
    store.dispatch(toggleCam(!isVideoEnabled));
    storeLocalStream.getVideoTracks()[0].enabled = !isVideoEnabled;
    wss.updateCameraStatus(!isVideoEnabled);
  }
};

export const toggleScreenShare = (isScreenSharingActive, screenSharingStream = null) => {
  wss.notifyRoomParticipantsForScreenShare(isScreenSharingActive);
  if (!isScreenSharingActive) {
    switchVideoTracks(localStream);
  } else {
    switchVideoTracks(screenSharingStream);
  }
};

const switchVideoTracks = (stream) => {
  try {
    for (let socketId in peers) {
      const peer = peers[socketId];
      if (peer && peer.streams[0]) {
        const oldTrack = peer.streams[0].getVideoTracks()[0];
        const newTrack = stream.getVideoTracks()[0];
        if (oldTrack && newTrack) {
          peer.replaceTrack(oldTrack, newTrack, peer.streams[0]);
        }
      }
    }
  } catch (error) {
    console.error("Error occurred when switching video tracks", error);
  }
};

// Function to switch audio track in all peer connections
export const switchAudioTrack = (newTrack) => {
  try {
    newTrack.enabled = !!store.getState().room.micState;
    // First update the local stream
    const audioTracks = localStream.getAudioTracks();
    if (audioTracks.length > 0) {
      audioTracks.forEach(track => {
        track.stop();
        localStream.removeTrack(track);
      });
    }
    localStream.addTrack(newTrack);

    // Then update all peer connections
    for (let socketId in peers) {
      const peer = peers[socketId];
      if (peer) {
        const senders = peer._pc.getSenders();
        const audioSender = senders.find(sender => sender.track?.kind === 'audio');
        if (audioSender) {
          audioSender.replaceTrack(newTrack);
        }
      }
    }
  } catch (error) {
    console.error('Error switching audio track:', error);
  }
};

// Function to switch video track in all peer connections
export const switchVideoTrack = (newTrack) => {
  try {
    // First update the local stream
    const videoTracks = localStream.getVideoTracks();
    if (videoTracks.length > 0) {
      videoTracks.forEach(track => {
        track.stop();
        localStream.removeTrack(track);
      });
    }
    localStream.addTrack(newTrack);

    // Then update all peer connections
    for (let socketId in peers) {
      const peer = peers[socketId];
      if (peer) {
        const senders = peer._pc.getSenders();
        const videoSender = senders.find(sender => sender.track?.kind === 'video');
        if (videoSender) {
          videoSender.replaceTrack(newTrack);
        }
      }
    }
  } catch (error) {
    console.error('Error switching video track:', error);
  }
};
